import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import React, { Component } from 'react';
import { withMessages } from 'react-message-source';
import { Button, MessageProps } from 'semantic-ui-react';
import { updateRapidTestProducts, updateVaccinations } from 'services/covidProductsServices';
import { encryptAzureFiles } from 'services/fixActions';
import {
  fixBookingHistoryStatus,
  fixContainerEmployeeForMisantoSelfServiceBookings,
  fixNotInvoicedInterventionBookings,
  fixSingleTests2021Product,
  populateBookingHistoryUserIds
} from 'services/interventionBookingServices';
import {
  migrateLegacyMediDataDownloadsDiamisanto,
  migrateLegacyMediDataDownloadsMisanto,
  recreateXmlBillsWithVersion44
} from 'services/mediDataServices';
import {
  migrateUnfitOrPrescriptionIntoPatientMedicalDocument
} from 'services/patientMedicalDocumentEditReportServices';
import { payCcPayedBillsGuarantor, payCcPayedBillsPatient } from 'services/paymentServices';
import { Privilege } from 'ts-types/api.enums';
import { BillPaymentResponse } from 'ts-types/api.types';

interface Props extends MessageProps, AuthConsumerRenderProps, NotificationSystemProps {
}

interface State {
  manualPaymentPdfData?: BillPaymentResponse,
  manualPaymentPdfModalOpen: boolean,
  cancelTokenSource: CancelTokenSource
}

class FixIssuesView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      manualPaymentPdfModalOpen: false,
      cancelTokenSource
    }
  }

  userHasPrivilege(privilege: Privilege): boolean {
    const {currentUser} = this.props;
    return currentUser!.userConfig.privileges.includes(privilege);
  }

  openManualPaymentPdfModal = (manualPaymentResponse: BillPaymentResponse) => {
    this.setState({
      manualPaymentPdfModalOpen: true,
      manualPaymentPdfData: manualPaymentResponse
    })
  };

  closeManualPaymentPdfModal = () => {
    const proofList = this.state.manualPaymentPdfData!.proofList;
    if (!proofList) {
      this.props.resetBills();
    } else {
      this.setState({
        manualPaymentPdfModalOpen: false,
        manualPaymentPdfData: undefined
      });
    }
  };

  updateRapidTests = async () => {

    const {selectionNotificationSystem, getMessageWithNamedParams, getMessage} = this.props;

    try {
      const response = await updateRapidTestProducts(this.state.cancelTokenSource);

      const successMessage = getMessageWithNamedParams("fix.issues.updateRapidTestSuccess",
          {
            created: response.created,
            updates: response.updates,
            rapidTestProducts: response.rapidTestProducts
          });

      //@ts-ignore
      const notification = selectionNotificationSystem.current;
      if (notification) {
        notification.addNotification({
          title: getMessage("fixIssuesView.update.success"),
          message: successMessage,
          level: "success"
        });
      }
    } catch (e) {

    }
  }

  updateVaccinations = async () => {

    const {selectionNotificationSystem, getMessageWithNamedParams, getMessage} = this.props;
    const {cancelTokenSource} = this.state;

    try {
      const response = await updateVaccinations(cancelTokenSource)
      const successMessage = getMessageWithNamedParams("fix.issues.updateVaccinationSuccess",
          {
            created: response.created,
            updates: response.updates,
            vaccinationProducts: response.newVaccineProducts
          });

      //@ts-ignore
      const notification = selectionNotificationSystem.current;
      if (notification) {
        notification.addNotification({
          title: getMessage("fixIssuesView.updateVaccination.success"),
          message: successMessage,
          level: "success"
        });
      }
    } catch (e) {

    }
  }

  handleMobileAppCcBills = async (billTo: string) => {

    const {getMessage} = this.props;
    const {cancelTokenSource} = this.state;

    let errorMessage;
    const targetMethod = billTo === "patient" ? payCcPayedBillsPatient : payCcPayedBillsGuarantor;
    await targetMethod(cancelTokenSource)
        .then(response => {
          if (response.pdfId) {
            this.openManualPaymentPdfModal(response);
          }
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  handlePopulateBookingHistoryUserIds = async () => {

    const {getMessage} = this.props;
    const {cancelTokenSource} = this.state;

    let errorMessage;
    await populateBookingHistoryUserIds(cancelTokenSource)
        .then(response => {
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  handleFixContainerEmployeeForMisantoSelfServiceBookings = async () => {

    const {getMessage} = this.props;
    const {cancelTokenSource} = this.state;

    let errorMessage;
    await fixContainerEmployeeForMisantoSelfServiceBookings(cancelTokenSource)
        .then(response => {
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  handleFixSingleTests2021Product = async () => {

    const {getMessage} = this.props;
    const {cancelTokenSource} = this.state;

    let errorMessage;
    await fixSingleTests2021Product(cancelTokenSource)
        .then(response => {
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  handleFixNotInvoicedInterventionBookings = async () => {

    const {getMessage} = this.props;
    const {cancelTokenSource} = this.state;

    let errorMessage;
    await fixNotInvoicedInterventionBookings(cancelTokenSource)
        .then(response => {
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  handleFixBookingHistoryStatus = async () => {

    const {getMessage} = this.props;
    const {cancelTokenSource} = this.state;

    let errorMessage;
    await fixBookingHistoryStatus(cancelTokenSource)
        .then(response => {
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  migrateLegacyMediDataDownloadsMisanto = async () => {

    const {getMessage} = this.props;
    const {cancelTokenSource} = this.state;

    let errorMessage;
    await migrateLegacyMediDataDownloadsMisanto(cancelTokenSource)
        .then(response => {
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  migrateLegacyMediDataDownloadsDiamisanto = async () => {

    const {getMessage} = this.props;
    const {cancelTokenSource} = this.state;

    let errorMessage;
    await migrateLegacyMediDataDownloadsDiamisanto(cancelTokenSource)
        .then(response => {
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  migrateXmlBillsWithVersion44 = async () => {

    const {currentUser, getMessage} = this.props;
    const {cancelTokenSource} = this.state;

    const mandateId = currentUser!.userConfig.mandateId;

    let errorMessage;
    await recreateXmlBillsWithVersion44(mandateId, cancelTokenSource)
        .then(response => {
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  migrateUnfitOrPrescriptionIntoCDR = async () => {
    const {getMessage, options} = this.props;
    const {cancelTokenSource} = this.state;

    let errorMessage;
    await migrateUnfitOrPrescriptionIntoPatientMedicalDocument(cancelTokenSource)
        .then(response => {
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  encryptAzureFiles = async () => {
    const {getMessage, options} = this.props;
    const {cancelTokenSource} = this.state;

    let errorMessage;
    await encryptAzureFiles(cancelTokenSource)
        .then(response => {
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  render(): React.ReactNode {
    return (
        <PraxStyledSegment>

          <div>
            <FormButtonsRow>
              {
                  this.userHasPrivilege(Privilege.PX_CORE_FIX_CC_PAYMENTS) &&
                  <>
                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.updateRapidTests()}
                    >
                      Update Rapid Tests
                    </Button>

                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.updateVaccinations()}
                    >
                      Update Vaccinations
                    </Button>
                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.handleMobileAppCcBills("patient")}
                    >
                      Fix CC Bills Patient
                    </Button>

                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.handleMobileAppCcBills("guarantor")}
                    >
                      Fix CC Bills Guarantor
                    </Button>

                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.handleFixSingleTests2021Product()}
                    >
                      Fix product for 2021 single tests
                    </Button>

                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.handleFixNotInvoicedInterventionBookings()}
                    >
                      Invoice not invoiced intervention bookings
                    </Button>
                  </>
              }

              {
                  this.userHasPrivilege(Privilege.PX_CORE_POPULATE_HISTORY_USER_IDS) &&
                  <>
                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.handlePopulateBookingHistoryUserIds()}
                    >
                      Populate booking history users
                    </Button>
                  </>
              }

              {
                  this.userHasPrivilege(Privilege.PX_CORE_POPULATE_HISTORY_USER_IDS) &&
                  <>
                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.handleFixBookingHistoryStatus()}
                    >
                      Fix Booking History Status
                    </Button>
                  </>
              }

              {
                  this.userHasPrivilege(Privilege.PX_CORE_POPULATE_HISTORY_USER_IDS) &&
                  <>
                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.handleFixContainerEmployeeForMisantoSelfServiceBookings()}
                    >
                      Fix SelfService Booking Container Employee
                    </Button>
                  </>
              }

              {
                  this.userHasPrivilege(Privilege.PX_CORE_POPULATE_HISTORY_USER_IDS) &&
                  <>
                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.migrateLegacyMediDataDownloadsMisanto()}
                    >
                      Migrate legacy MediData downloads (MiSANTO)
                    </Button>
                  </>
              }

              {
                  this.userHasPrivilege(Privilege.PX_CORE_POPULATE_HISTORY_USER_IDS) &&
                  <>
                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.migrateLegacyMediDataDownloadsDiamisanto()}
                    >
                      Migrate legacy MediData downloads (DiAMiSANTO)
                    </Button>
                  </>
              }

              {
                  this.userHasPrivilege(Privilege.PX_CORE_ARCHIVE_REJECTED_BILL_AND_RECREATE_XML) &&
                  <>
                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.migrateXmlBillsWithVersion44()}
                    >
                      Migrate MediData request with ver. 4.4
                    </Button>
                  </>
              }

              {
                  this.userHasPrivilege(Privilege.PX_CORE_UNABLE_TO_WORK_OR_PRESCRIPTION_REPORT) &&
                  <>
                    <Button compact
                            type="button"
                            color="orange"
                            onClick={() => this.migrateUnfitOrPrescriptionIntoCDR()}
                    >
                      Migrate Unfit/Prescription into CDR
                    </Button>
                  </>
              }

              {/*<>
                <Button compact
                        type="button"
                        color="orange"
                        onClick={() => this.encryptAzureFiles()}
                >
                  Encrypt Azure files
                </Button>
              </>*/}
            </FormButtonsRow>
          </div>
        </PraxStyledSegment>
    );
  }

}

export default withMessages(withAuthContext(withNotificationSystemContext(FixIssuesView)));
